import {
    Component,
    ChangeDetectionStrategy,
    ViewEncapsulation,
    ChangeDetectorRef,
    ViewChild,
    ElementRef
} from "@angular/core";

import { IrbEditor } from "@irb/irb-editors/irb-editors.base";
import { IEvent, IrbAppEvents, IrbClientsEvents, IrbDialogsEvents, IrbEditorsEvents,IrbModelsEvents} from "@irb/irb-common/events";
import { IMultiPFAssignmentsData, IAutoRebalanceDetails,IModelDetail } from "../irb-editors.model";
import { PortfolioService } from "@irb/irb-clients/portfolios/portfolio.service";
import { AnalyticsService} from "@irb-common/analytics/analytics.service";
import { IrbSettingsService} from "@irb/irb-settings/irb-settings.service";
import { IAlertDialogData} from "@irb/app.model";
import { Observable, of} from "rxjs";
import { IGlobalSettings} from "@irb/irb-settings/irb-settings.model";
import { IMultiplePortfolioEditDto, IMultiplePortfolioEditRequest } from "@irb/irb-clients/irb-clients.model";

@Component({ standalone: false,
    selector: "irb-editor-multiple-portfolios",
    templateUrl: "./editor-multiple-portfolios.component.html",
    styleUrls: ["./editor-multiple-portfolios.component.scss", "../../irb-common/styles/dialog.scss", "../editor-sidebar/editor-sidebar.component.scss"],
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.None
})
export class EditorMultiplePortfoliosComponent extends IrbEditor {
    appConfig: any = {};
    saveMultiPfThreshold = 99;
    @ViewChild("body") body: ElementRef;

    portfolioIds: Array<number>;
    _assignmentsData: IMultiPFAssignmentsData;
    defaultExpandedPanels: Array<string> = ["SETTINGS", "CUSTOM_LABELS", "GAINS_MANAGEMENT", "AUTOMATED_REBALANCING"];
    isUiTest = false;
    autoRebalanceData: IAutoRebalanceDetails;
    isAutoRebalanceEnabled: boolean;
    isTLHSetInGlobal: boolean;
    updateStatusCount = 0;
    progressTimer: any;
    restrictedPortfolios: Array<any> = [];
    globalSettings: IGlobalSettings;
    isGlobalSettings: boolean = false;
    multiplePortfolioEditRequest: IMultiplePortfolioEditRequest;
    selectedModel: IModelDetail = {} as IModelDetail;
    reloadAfterModelClosed: boolean = false;
    isGainsManagementValid: boolean = true;
    ENABLE_INVCASH_AUTO: boolean = false;

    constructor(private cdr: ChangeDetectorRef,
        private readonly portfolioService: PortfolioService,
        public ats: AnalyticsService,
                public iss: IrbSettingsService) {
        super();
        this.identifier = "ID";
    }
    
    setDefaultAutoRebalance() {
        this.autoRebalanceData = {
            isAutoRebalEnabled: null,
            autoRebalType: '',
            autoRebalRequestedBy: '',
            autoRebalStartDate: '',
            autoRebalNextRunDate: '',
            autoScheduler: {
                createdBy: null,
                createdDate: null,
                portfolioId: null,
                environmentId: null,
                autoSchedulerId: null,
                completedRunTime: null,
                modifiedBy: null,
                modifiedDate: null,
                dateNumber: null,
                dayName: null,
                endAfterNTimes: null,
                endByDate: null,
                endDateType: null,
                everyNDays: 1,
                everyNMonths: null,
                everyNWeeks: null,
                monday: false,
                tuesday: false,
                wednesday: false,
                thursday: false,
                friday: false,
                saturday: false,
                sunday: false,
                isActive: true,
                monthlyRecurrenceType: null,
                nextRunDate: null,
                recurringFreqType: null,
                scheduleType: null,
                startDate: null,
                version: 0,
                everyDay: null,
                everyWeekday: null,
                isDeleted: false
            }
        };
    }

    initExpansionPanels() {
        let dep = this.defaultExpandedPanels;
        let sessionOverridesDefault = true;
        this.ies.loadExpansionState(1, "editor-multiple-portfolios", dep, sessionOverridesDefault);
    }

    get(event: IEvent) {
        this.setDefaultAutoRebalance();
        super.get(event);
        this.appConfig = this.as.getItem("IRB_APP_CONFIG");
        if (this.appConfig?.saveMultiPfThreshold) {this.saveMultiPfThreshold = this.appConfig?.saveMultiPfThreshold;}
        this.ENABLE_INVCASH_AUTO = this.appConfig?.featureFlags?.ENABLE_INVCASH_AUTO === true;

        this.iss.getGlobalSettings().subscribe((globalSettings: any) => {
            this.globalSettings = globalSettings;
            this.isGlobalSettings = true;
            this.isAutoRebalanceEnabled = globalSettings?.isAutoRebalanceEnabled;
            this.isTLHSetInGlobal = globalSettings.isTlhLotLevel || globalSettings.isTlhPositionLevel;
        });

        this.ats.trackEditMultiPlePortfoliosEvent();
        this.initExpansionPanels();
        
       if(event.data) {
        this.multiplePortfolioEditRequest = event.data.data;

        let data = {
            securityModelId: null,
            labels: null,
            gainsControlSetting: null,
            gainsThresholdType: null,
            gainsThresholdAmt: null,
            isAutoRebalEnabled: null,
            autoRebalNextRunDate: null,
            autoRebalRequestedBy: null,
            autoRebalStartDate: null,
            autoRebalType: null,
            autoScheduler: null,
            actBlockAutoSchd: null
        } as IMultiplePortfolioEditDto;

        this.load(data);
       }
    } 

    load(data: IMultiplePortfolioEditDto) {
        super.load(data);
        this.cdr.detectChanges();
        this.onResize();
    }

    save() {
        this.validateInput().subscribe(errors => {
            if (errors.length > 0) {
                this.as.fireEvent(IrbAppEvents.SHOW_ALERT, {
                    dType: 'ERROR',
                    dTitle: 'Alert',
                    dProceedBtnLabel: 'Close',
                    msg: errors.join('\n'),
                    onProceed: () => {
                    }
                } as IAlertDialogData);
                return;
            } else {
                this.saveMultiplePortfoliosAsync();
            }
        });
    }

    saveMultiplePortfoliosAsync() {
        this.as.fireEvent(IrbEditorsEvents.SAVE_START);                
        this.multiplePortfolioEditRequest.portfolioDto = this.data;

        this.portfolioService.editMultiplePortfoliosAsync(this.multiplePortfolioEditRequest).subscribe((response: any) => {
            this.updateStatusCount = 0;
            this.progressTimer && clearTimeout(this.progressTimer);
            if (response.responseUtilityMap?.statusId) {
                this.actionStatusCheckIntervalStart(response.responseUtilityMap?.statusId);
            } else {
                this.actionHandleError();
            }
        }, () => {
            this.actionHandleError();
        });
    }

    actionStatusCheckIntervalStart(statusId: any) {
        this.portfolioService.editMultiplePFStatus(statusId).subscribe((statusResponse: any) => {
            if (statusResponse.responseStatus === "SUCCESS") {

                let reqStatus = 'FAILED';
                if (statusResponse.responseUtilityMap && statusResponse.responseUtilityMap.requestStatus) {
                    reqStatus = statusResponse.responseUtilityMap.requestStatus.trim().toUpperCase();
                } else if (statusResponse.responseData && statusResponse.responseData.reqStatus) {
                    reqStatus = statusResponse.responseData.reqStatus.trim().toUpperCase();
                }

                if (reqStatus === 'STARTED' || reqStatus === 'NEW' || reqStatus === 'IN_PROGRESS') {
                    let timeout = (this.updateStatusCount > 10) ? 8000 : (this.updateStatusCount > 2) ? 3000 : 1500;
                    this.updateStatusCount++;
                    if (!this.isUiTest) {
                        this.progressTimer = setTimeout(() => {
                            this.actionStatusCheckIntervalStart(statusId);
                        }, timeout);
                    }
                } else if (reqStatus === 'COMPLETED') {
                    this.progressTimer && clearTimeout(this.progressTimer);
                    this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
                    this.saveMultiplePortfoliosFinish();
                    this.close();
                } else {
                    this.actionHandleError();
                }
            } else {
                this.actionHandleError();
            }
        }, () => {
            this.close();
            this.as.fireEvent(IrbAppEvents.HIDE_WAIT);
            this.progressTimer && clearTimeout(this.progressTimer);
        });
        
    }

    actionHandleError() {
        this.as.fireEvent(IrbEditorsEvents.SAVE_ERROR, {"slot": this.slot});
    }

    saveMultiplePortfoliosFinish() {
        this.as.fireEvent(IrbEditorsEvents.SAVE_SUCCESS, {
            "hasPendingFunction": this.hasPendingFunction,
            "data": "Portfolio updated.",
            "slot": this.slot
        });
        this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, {"slot": this.slot});
        this.as.fireEvent(IrbAppEvents.GRID_REFRESH, {"slot": this.slot});
    }

    closeAndReopen(multiplePortfolioEditRequest: IMultiplePortfolioEditRequest) {
      this.as.fireEvent(IrbEditorsEvents.CLOSE_WITHOUT_SAVE, {"slot": this.slot})
      if (this.reloadAfterModelClosed) {
        this.reloadAfterModelClosed = false;
        this.as.fireEvent(IrbClientsEvents.SHOW_EDIT_MULTIPLE_PORTFOLIOS, {data: multiplePortfolioEditRequest});
      }
    }

    validateInput():Observable<string[]>  {
        const errors: string[] = [];

        if (this.isAutoRebalanceEnabled && this.autoRebalanceData) {
            if (this.autoRebalanceData.isAutoRebalEnabled && this.autoRebalanceData.autoScheduler?.recurringFreqType === 'Weekly') {
                if (!this.autoRebalanceData.autoScheduler.monday 
                    && !this.autoRebalanceData.autoScheduler.tuesday 
                    && !this.autoRebalanceData.autoScheduler.wednesday 
                    && !this.autoRebalanceData.autoScheduler.thursday 
                    && !this.autoRebalanceData.autoScheduler.friday 
                    && !this.autoRebalanceData.autoScheduler.saturday 
                    && !this.autoRebalanceData.autoScheduler.sunday) {
                    errors.push('Please select day(s) of the week for the automated rebalance');
                }
            }
        }

        if(!this.isGainsManagementValid) {
            errors.push('Gains Threshold Amount cannot be empty or invalid.');
        }

        return of(errors);
    }

    get assignmentsData() {
        return this._assignmentsData;
    }

    set assignmentsData(newAssignemntsData: any) {
        this._assignmentsData = newAssignemntsData;
    }

    onAutoRebalanceChange() {
        this.data.isAutoRebalEdited = true;
        this.data.isAutoRebalEnabled = this.autoRebalanceData.isAutoRebalEnabled;
        this.data.autoScheduler = this.autoRebalanceData.autoScheduler;
        this.data.autoRebalType = this.autoRebalanceData.autoRebalType;
        if (this.ENABLE_INVCASH_AUTO) {
            this.data.investCashSetting = this.autoRebalanceData.investCashSetting;
         } else {
           delete this.data.investCashSetting;
         }
        this.data.autoRebalRequestedBy = this.autoRebalanceData.autoRebalRequestedBy;
        this.data.autoRebalStartDate = this.autoRebalanceData.autoRebalStartDate;
        this.data.autoRebalNextRunDate = this.autoRebalanceData.autoRebalNextRunDate;
        this.onInputChange();
    }

    showPortfolioMultieditView() {
        this.as.fireEvent(IrbDialogsEvents.SHOW_PORTFOLIO_MULTIEDIT_VIEW, {
            data: this.util.clone(this.multiplePortfolioEditRequest)
        });
    }
    onSelectedModelChange(model: IModelDetail) {
      this.selectedModel = model;
    }

    viewPortfolioModel() {
      let data: any =  {};
      data.slot ="B";
      data.isDraft = false;

      if(this.selectedModel && this.selectedModel.modelType ==="Blended") {
        let modelId = this.selectedModel.securityModelId.replace('b','').replace('B','');
        data.blendedSecurityModelId = modelId;
        data.modelId = modelId;
        data.type = this.selectedModel.modelType;
      } else {
        data.modelId = this.selectedModel.securityModelId;
        data.type = "Base";
      }
      if (!this.isModified) {
        this.as.fireEvent(IrbModelsEvents.SHOW_EDIT_MODEL, data);
      } else {
        this.reloadAfterModelClosed = true;
        this.checkForModifications(() => {
          this.as.fireEvent(IrbModelsEvents.SHOW_EDIT_MODEL, data);
          this.closeAndReopen(this.multiplePortfolioEditRequest);
        });
      }
    }
    // public setter (for unit test access)
    public setIsModified(value: boolean): void {
      this.isModified = !!value;
    }
}
